import { BadgeCheck, Ban, MoveUpRight, Redo2 } from "lucide-react";
import { Button } from "../ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
  CardContent,
} from "../ui/card";
import { Route as salesOneRoute } from "@/routes/(pharma)/_layout/overview/sales_history/$saleId";
import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { markSaleAsRefunded, markSaleAsVoid } from "@/routes/(pharma)/_layout/overview/sales_history/$saleId/-data/api";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/use-toast";
import { RefundMode, SaleStatus } from "@/gen/sales/common_pb";
import dayjs from "dayjs";
import { pharmacyClient } from "@/connect";
import { Input } from "../ui/input";
import { ItemType } from "@/gen/inventory/common_pb";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";

export default function QuickActions({ partialMedicalItemsSelected }: { partialMedicalItemsSelected: {
    saleItemId: string;
    itemType: ItemType;
    quantity: number;
  }[] }) {
  const data = salesOneRoute.useLoaderData();
  const params = salesOneRoute.useParams();
  const navigate = useNavigate();
  const router = useRouter();
  const searchParams = salesOneRoute.useSearch();
  const [voidTransactionLoading, setVoidTransactionLoading] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [refundMode, setRefundMode] = useState<RefundMode>(RefundMode.UNKNOWN);

  console.log(refundMode)

  return (
    <Card className="mt-6 sticky bottom-0 overflow-hidden rounded-xl shadow-transparent ml-4">
      <CardContent className="flex gap-2 p-4 space-y-0">
        <Link
          to="/receipt/$saleId"
          search={{ return_to: "sales_history_one" }}
          disabled={data.saleValues?.isVoided}
          preload="intent"
        >
          <Button
            size="sm"
            variant="ghost"
            className="gap-1 bg-blue-700 text-white border border-gray-200 rounded-lg"
            disabled={data.saleValues?.isVoided}
          >

            <BadgeCheck className="p-1"></BadgeCheck>
            Show Receipt
          </Button>
        </Link>
        <Popover>
          <PopoverTrigger disabled={data.saleValues?.isVoided}>
            <Button
            size="sm"
            variant="ghost"
            className="gap-1 bg-red-600 border border-gray-200 rounded-lg justify-left items-left text-white"
            disabled={voidTransactionLoading || data.saleValues?.isVoided}
          >
            <Ban className="p-1"></Ban>
            {voidTransactionLoading ? "Voiding Transaction..." : "Void Transaction"}
          </Button>
          <PopoverContent>
            <p>Are you sure you want to void this sale?</p>
            <p>This action cannot be undone.</p>
            <Input className="mt-2" placeholder="Enter reason for voiding" value={voidReason} onChange={(e) => setVoidReason(e.target.value)}/>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="bg-red-600 text-white border border-gray-200 rounded-lg"
                disabled={voidTransactionLoading || data.saleValues?.status === SaleStatus.VOIDED || data.saleValues?.createdAt?.toDate().getTime()! < dayjs().subtract(1, "day").toDate().getTime()}
                onClick={async () => {
                  try {
                    if (voidReason.trim() === "") {
                      toast({
                        title: "Error",
                        variant: "destructive",
                        description: "Please enter a reason for voiding the transaction.",
                      });
                      return;
                    }
                    setVoidTransactionLoading(true);
                    await pharmacyClient.voidSale({
                      saleId: params.saleId,
                      reason: voidReason,
                    })
                    setVoidTransactionLoading(false);
                    router.invalidate();
                  } catch (e) {
                    toast({
                      title: "Error",
                      variant: "destructive",
                      description: "Failed to void transaction",
                    });
                    console.error(e);
                    setVoidTransactionLoading(false);
                  }
                }}
              >
                {voidTransactionLoading ? "Voiding Transaction..." : "Yes"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="bg-gray-200 text-black border border-gray-200 rounded-lg"
              >
                No
              </Button>
            </div>
          </PopoverContent>
          </PopoverTrigger>
        </Popover>
        {data.isValidPatient && <Button
          size="sm"
          variant="ghost"
          className="gap-1 w-auto p-3 bg-white border border-gray-200 rounded-lg"
          onClick={() => {
            navigate({
              to: "/patients/patient-info/$patientId",
              params: { patientId: data.patientId },
            });
          }}
        >
          <MoveUpRight className="p-1"></MoveUpRight>
          View Other Transactions
        </Button>}
        <Popover>
          <PopoverTrigger disabled={data.saleValues?.isVoided}>
            <Button
              size="sm"
              variant="ghost"
              className="gap-1 w-auto p-3 text-black border border-gray-200 rounded-lg"
              disabled={voidTransactionLoading || data.saleValues?.isVoided || data.saleValues?.status === SaleStatus.FULLY_REFUNDED}
            >
              <Redo2 className="p-1"></Redo2>
              Return & Refund
            </Button>
          <PopoverContent>
            <p>Are you sure you want to return and refund?</p>
            <p>{
              (partialMedicalItemsSelected.length === 0) ? "All Items will be refunded." : "Selected Items will be refunded."
            }</p>
            <p>{
              (partialMedicalItemsSelected.length > 0) ? `Selected Items: ${partialMedicalItemsSelected.length}` : ""
            }</p>
            <Input className="mt-2" placeholder="Enter reason for refunding" value={voidReason} onChange={(e) => setVoidReason(e.target.value)}/>
            <Select value={refundMode.toString()} onValueChange={(value) => setRefundMode(Number(value) as RefundMode)}>
              <SelectTrigger className="w-full">
                {refundMode === RefundMode.UNKNOWN ? "Select Refund Mode" : RefundMode[refundMode]}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={RefundMode.CASH.toString()}>
                  Cash
                </SelectItem>
                <SelectItem value={RefundMode.CARD_REVERSAL.toString()}>
                  Card Reversal
                </SelectItem>
                <SelectItem value={RefundMode.STORE_CREDIT.toString()}>
                  Store Credit
                </SelectItem>
                <SelectItem value={RefundMode.BANK_TRANSFER.toString()}>
                  Bank Transfer
                </SelectItem>
                <SelectItem value={RefundMode.ADJUSTMENT_NOTE.toString()}>
                  Adjustment Note
                </SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="bg-red-600 text-white border border-gray-200 rounded-lg"
                disabled={voidTransactionLoading}
                onClick={async () => {
                  try {
                    setVoidTransactionLoading(true);
                    if (voidReason.trim() === "") {
                      toast({
                        title: "Error",
                        variant: "destructive",
                        description: "Please enter a reason for refunding the transaction.",
                      });
                      return;
                    }

                    let finalRefundItems = partialMedicalItemsSelected

                    if (partialMedicalItemsSelected.length === 0) {
                      finalRefundItems = data.items.filter(i => !i.isRefunded).map((item) => {
                        return {
                          saleItemId: item.saleItemId,
                          itemType: item.itemType,
                          quantity: item.quantity,
                        };
                      });
                    }

                    await pharmacyClient.refundSale({
                      items: finalRefundItems.map((item) => ({
                        itemId: item.saleItemId,
                        itemType: item.itemType,
                        quantity: item.quantity,
                        refundMode: refundMode
                      })),
                      saleId: params.saleId,
                      reason: voidReason,
                    });
                    setVoidTransactionLoading(false);
                    router.invalidate();
                    toast({
                      title: "Success",
                      description: "Transaction has been successfully refunded.",
                      variant: "success",
                      duration: 3000,
                    });
                  } catch (e) {
                    toast({
                      title: "Error",
                      variant: "destructive",
                      description: "Failed to refund transaction",
                    })
                    console.error(e);
                    setVoidTransactionLoading(false);
                  }
                }}
              >
                {voidTransactionLoading ? "Voiding Transaction..." : "Yes"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="bg-gray-200 text-black border border-gray-200 rounded-lg"
              >
                No
              </Button>
            </div>
          </PopoverContent>
          </PopoverTrigger>
        </Popover>
      </CardContent>
    </Card>
  );
};
